import { useEffect } from "react";

/**
 * Custom hook to dynamically update favicons based on admin settings
 * @param {Object} faviconIcons - Object containing favicon URLs from admin settings
 */
const useFavicon = (faviconIcons) => {
  useEffect(() => {
    // Helper function to remove a link tag
    const removeLinkTag = (selector) => {
      const link = document.querySelector(selector);
      if (link) {
        link.remove();
      }
    };

    // If no faviconIcons provided, remove all favicon links
    if (!faviconIcons) {
      removeLinkTag('link[rel="icon"][type="image/x-icon"]');
      removeLinkTag('link[rel="shortcut icon"]');
      removeLinkTag('link[rel="icon"][sizes="32x32"]');
      removeLinkTag('link[rel="icon"][sizes="16x16"]');
      removeLinkTag('link[rel="apple-touch-icon"]');
      removeLinkTag('link[rel="manifest"]');
      return;
    }

    // Helper function to update or create a link tag
    const updateLinkTag = (selector, href, rel, sizes, type) => {
      let link = document.querySelector(selector);

      if (link) {
        // Update existing link
        link.href = href;
      } else {
        // Create new link if it doesn't exist
        link = document.createElement("link");
        link.rel = rel;
        if (sizes) link.sizes = sizes;
        if (type) link.type = type;
        link.href = href;
        document.head.appendChild(link);
      }
    };

    // Update favicon.ico (main favicon)
    if (faviconIcons.faviconIco) {
      updateLinkTag(
        'link[rel="icon"][type="image/x-icon"]',
        faviconIcons.faviconIco,
        "icon",
        null,
        "image/x-icon"
      );

      // Also update shortcut icon
      updateLinkTag(
        'link[rel="shortcut icon"]',
        faviconIcons.faviconIco,
        "shortcut icon",
        null,
        "image/x-icon"
      );
    } else {
      removeLinkTag('link[rel="icon"][type="image/x-icon"]');
      removeLinkTag('link[rel="shortcut icon"]');
    }

    // Update 32x32 favicon
    if (faviconIcons.favicon32) {
      updateLinkTag(
        'link[rel="icon"][sizes="32x32"]',
        faviconIcons.favicon32,
        "icon",
        "32x32",
        "image/png"
      );
    } else {
      removeLinkTag('link[rel="icon"][sizes="32x32"]');
    }

    // Update 16x16 favicon
    if (faviconIcons.favicon16) {
      updateLinkTag(
        'link[rel="icon"][sizes="16x16"]',
        faviconIcons.favicon16,
        "icon",
        "16x16",
        "image/png"
      );
    } else {
      removeLinkTag('link[rel="icon"][sizes="16x16"]');
    }

    // Update Apple Touch Icon
    if (faviconIcons.appleTouchIcon) {
      updateLinkTag(
        'link[rel="apple-touch-icon"]',
        faviconIcons.appleTouchIcon,
        "apple-touch-icon",
        "180x180"
      );
    } else {
      removeLinkTag('link[rel="apple-touch-icon"]');
    }

    // Update Android Chrome icons by creating/updating manifest dynamically
    if (faviconIcons.androidChrome192 || faviconIcons.androidChrome512) {
      const manifest = {
        name: process.env.REACT_APP_SITE_NAME || "Admin Panel",
        short_name: process.env.REACT_APP_SITE_NAME || "Admin",
        icons: [],
        theme_color: "#ffffff",
        background_color: "#ffffff",
        display: "standalone",
      };

      if (faviconIcons.androidChrome192) {
        manifest.icons.push({
          src: faviconIcons.androidChrome192,
          sizes: "192x192",
          type: "image/png",
        });
      }

      if (faviconIcons.androidChrome512) {
        manifest.icons.push({
          src: faviconIcons.androidChrome512,
          sizes: "512x512",
          type: "image/png",
        });
      }

      // Create a blob URL for the manifest
      const manifestBlob = new Blob([JSON.stringify(manifest)], {
        type: "application/json",
      });
      const manifestURL = URL.createObjectURL(manifestBlob);

      // Update manifest link
      let manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        manifestLink.href = manifestURL;
      } else {
        manifestLink = document.createElement("link");
        manifestLink.rel = "manifest";
        manifestLink.href = manifestURL;
        document.head.appendChild(manifestLink);
      }
    } else {
      removeLinkTag('link[rel="manifest"]');
    }
  }, [faviconIcons]);
};

export default useFavicon;
